import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ContactForm } from '../components/ContactForm';
import { DataTable, type Column } from '../components/DataTable';
import { Button, ErrorBox, Field, FieldGroup, Input, Modal, PageHeader, Pill, SearchInput, Select, Spinner } from '../components/ui';
import { fetchAll } from '../lib/fetchAll';
import { CONTACT_TYPES, personName, phone, relative } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';

async function loadContacts() {
  return fetchAll((from, to) =>
    supabase
      .from('contacts')
      .select(
        'id, first_name, last_name, display_name, title, contact_types, tags, needs_review, last_contacted_at, current_org_id, organizations(id, name), contact_emails(email, is_primary), contact_phones(phone_e164, extension, is_primary)',
      )
      .order('last_name', { nullsFirst: false })
      .range(from, to),
  );
}
type Row = Awaited<ReturnType<typeof loadContacts>>[number];

function primary<T extends { is_primary: boolean }>(list: T[]): T | undefined {
  return list.find((x) => x.is_primary) ?? list[0];
}

export function Contacts() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['contacts'], queryFn: loadContacts });
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [special, setSpecial] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((c) => {
      if (type === 'untyped' ? c.contact_types.length > 0 : type && !c.contact_types.includes(type)) return false;
      if (special === 'review' && !c.needs_review) return false;
      if (special === 'no-email' && c.contact_emails.length > 0) return false;
      if (special === 'no-company' && c.current_org_id) return false;
      if (!needle) return true;
      return [personName(c), c.display_name, c.organizations?.name, c.title, ...c.contact_emails.map((e) => e.email), ...c.tags]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(needle));
    });
  }, [data, q, type, special]);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (c) => `${c.last_name ?? ''} ${c.first_name ?? ''}`.trim().toLowerCase() || c.display_name?.toLowerCase(),
      render: (c) => (
        <div>
          <span className="font-medium text-slate-900">{personName(c)}</span>
          {c.needs_review && (
            <Pill color="orange" className="ml-2">
              review
            </Pill>
          )}
          {c.title && <div className="text-xs text-slate-500">{c.title}</div>}
        </div>
      ),
    },
    {
      key: 'company',
      header: 'Company',
      sortValue: (c) => c.organizations?.name?.toLowerCase(),
      render: (c) =>
        c.organizations ? (
          <Link to={`/companies/${c.organizations.id}`} className="hover:text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>
            {c.organizations.name}
          </Link>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: 'type',
      header: 'Type',
      sortValue: (c) => c.contact_types.join(','),
      render: (c) => (
        <div className="flex flex-wrap gap-1">
          {c.contact_types.map((t) => (
            <Pill key={t} color="blue" className="capitalize">
              {t}
            </Pill>
          ))}
          {c.tags.map((t) => (
            <Pill key={t}>{t}</Pill>
          ))}
        </div>
      ),
    },
    { key: 'email', header: 'Email', sortValue: (c) => primary(c.contact_emails)?.email, render: (c) => primary(c.contact_emails)?.email ?? <span className="text-slate-300">—</span> },
    {
      key: 'phone',
      header: 'Phone',
      render: (c) => {
        const p = primary(c.contact_phones);
        return p ? phone(p.phone_e164, p.extension) : <span className="text-slate-300">—</span>;
      },
      className: 'whitespace-nowrap',
    },
    {
      key: 'last',
      header: 'Last contact',
      sortValue: (c) => (c.last_contacted_at ? -new Date(c.last_contacted_at).getTime() : null),
      render: (c) => <span className="whitespace-nowrap text-slate-500">{relative(c.last_contacted_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle={data ? `${rows.length} of ${data.length}` : undefined}
        actions={
          <>
            {selected.size > 0 && <Button onClick={() => setBulkOpen(true)}>Edit {selected.size} selected</Button>}
            <Button variant="primary" onClick={() => setCreating(true)}>
              New contact
            </Button>
          </>
        }
      />
      <div className="mb-3 flex flex-wrap gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="Filter by name, company, email, tag…" className="!w-72" />
        <Select value={type} onChange={(e) => setType(e.target.value)} className="!w-40">
          <option value="">All types</option>
          <option value="untyped">No type yet</option>
          {CONTACT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t[0]!.toUpperCase() + t.slice(1)}
            </option>
          ))}
        </Select>
        <Select value={special} onChange={(e) => setSpecial(e.target.value)} className="!w-44">
          <option value="">Everyone</option>
          <option value="review">Flagged for review</option>
          <option value="no-email">No email</option>
          <option value="no-company">No company</option>
        </Select>
      </div>
      <ErrorBox error={error} />
      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/contacts/${c.id}`)}
          initialSort={{ key: 'name', dir: 'asc' }}
          selected={selected}
          onToggleSelect={(keys, on) =>
            setSelected((s) => {
              const n = new Set(s);
              keys.forEach((k) => (on ? n.add(k) : n.delete(k)));
              return n;
            })
          }
        />
      )}
      <Modal title="New contact" open={creating} onClose={() => setCreating(false)} wide>
        <ContactForm onDone={(id) => navigate(`/contacts/${id}`)} />
      </Modal>
      <BulkEdit
        ids={[...selected]}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          setBulkOpen(false);
          setSelected(new Set());
          qc.invalidateQueries({ queryKey: ['contacts'] });
        }}
      />
    </div>
  );
}

/** Set type and add/remove tags on many contacts at once (Section 6.1). */
function BulkEdit({ ids, open, onClose, onDone }: { ids: string[]; open: boolean; onClose: () => void; onDone: () => void }) {
  const [addTypes, setAddTypes] = useState<string[]>([]);
  const [addTags, setAddTags] = useState('');
  const [removeTags, setRemoveTags] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      const current = unwrap(await supabase.from('contacts').select('id, contact_types, tags').in('id', ids));
      const plus = addTags.split(',').map((t) => t.trim()).filter(Boolean);
      const minus = new Set(removeTags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean));
      for (const c of current) {
        const contact_types = [...new Set([...c.contact_types, ...addTypes])];
        const tags = [...new Set([...c.tags, ...plus])].filter((t) => !minus.has(t.toLowerCase()));
        unwrap(await supabase.from('contacts').update({ contact_types, tags }).eq('id', c.id));
      }
    },
    onSuccess: () => {
      setAddTypes([]);
      setAddTags('');
      setRemoveTags('');
      onDone();
    },
  });
  return (
    <Modal title={`Edit ${ids.length} contacts`} open={open} onClose={onClose}>
      <div className="space-y-4">
        <FieldGroup label="Add type">
          <div className="flex flex-wrap gap-1.5">
            {CONTACT_TYPES.map((t) => {
              const on = addTypes.includes(t);
              return (
                <button key={t} type="button" onClick={() => setAddTypes(on ? addTypes.filter((x) => x !== t) : [...addTypes, t])}>
                  <Pill color={on ? 'indigo' : 'gray'} className="capitalize">
                    {t}
                  </Pill>
                </button>
              );
            })}
          </div>
        </FieldGroup>
        <Field label="Add tags" hint="Comma-separated">
          <Input value={addTags} onChange={(e) => setAddTags(e.target.value)} />
        </Field>
        <Field label="Remove tags" hint="Comma-separated">
          <Input value={removeTags} onChange={(e) => setRemoveTags(e.target.value)} />
        </Field>
        <ErrorBox error={save.error} />
        <div className="flex justify-end">
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!addTypes.length && !addTags.trim() && !removeTags.trim()}>
            Apply to {ids.length}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
